/**
 * Whole-number XP per skill for a self-graded quiz, summing to the rounded total.
 *
 * Rounding each skill on its own overshoots: weights 16.67 + 16.67 + 66.66 at a score of 100 round
 * to 17 + 17 + 67 = 101. Each skill gets its floor instead, and the points still missing from
 * `Math.round(total)` go one each to the skills with the largest fractional parts (largest
 * remainder), so the sum never exceeds the lesson's weight at the given score.
 *
 * @param weights - The lesson's skill weights, in order (e.g. `[16.67, 16.67, 66.66]`).
 * @param score - Quiz score, 0–100.
 * @returns XP per skill, in the same order as `weights`.
 */
export function allocateQuizXp(weights: number[], score: number): number[] {
  const raw = weights.map((weight) => (weight * score) / 100)
  const allocated = raw.map((value) => Math.floor(value))

  const target = Math.round(raw.reduce((sum, value) => sum + value, 0))
  let missing = target - allocated.reduce((sum, value) => sum + value, 0)

  const byRemainder = raw
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder)

  for (const { index } of byRemainder) {
    if (missing <= 0) break
    allocated[index] = (allocated[index] ?? 0) + 1
    missing -= 1
  }

  return allocated
}
